/**
 * Estate Flow - Stripe Autopay Enrollment Component
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, CreditCard, Check, X, AlertCircle, Loader2, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StripeAutopayEnrollment = ({ tenantData, onEnrollmentChange }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [enrollmentForm, setEnrollmentForm] = useState({
    property_id: '',
    unit_id: '',
    amount: '',
    frequency: 'monthly',
    start_date: '',
    card_number: '',
    card_exp_month: '',
    card_exp_year: '',
    card_cvv: '',
    send_receipt_email: true,
    send_reminder_email: true
  });

  useEffect(() => {
    fetchAutopayStatus();
  }, []);

  const fetchAutopayStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/tenant/autopay/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrollments(response.data.enrollments || []);
    } catch (error) {
      console.error('Error fetching autopay status:', error);
      toast.error('Failed to load autopay status');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollment = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!enrollmentForm.amount || !enrollmentForm.start_date) {
      toast.error('Please fill in amount and start date');
      return;
    }

    if (!enrollmentForm.card_number || !enrollmentForm.card_exp_month || 
        !enrollmentForm.card_exp_year || !enrollmentForm.card_cvv) {
      toast.error('Please fill in all card details');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      // NOTE: In production, you should use Stripe.js/Elements for PCI compliance
      // This is a simplified version for demonstration
      
      // For now, we'll create a subscription with card details
      // In real implementation, use Stripe Payment Intents or Setup Intents
      
      const enrollData = {
        property_id: tenantData?.property_id || enrollmentForm.property_id,
        unit_id: tenantData?.unit_id || enrollmentForm.unit_id,
        amount: parseFloat(enrollmentForm.amount),
        frequency: enrollmentForm.frequency,
        start_date: enrollmentForm.start_date,
        card_last_4: enrollmentForm.card_number.slice(-4),
        card_type: detectCardType(enrollmentForm.card_number),
        card_exp_month: enrollmentForm.card_exp_month,
        card_exp_year: enrollmentForm.card_exp_year,
        send_receipt_email: enrollmentForm.send_receipt_email,
        send_reminder_email: enrollmentForm.send_reminder_email,
        // payment_method_id would come from Stripe.js in production
      };

      const response = await axios.post(
        `${API}/tenant/subscription/enroll`,
        enrollData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Automatic payments activated!', {
          description: `Your ${enrollmentForm.frequency} payments of $${enrollmentForm.amount} are now active.`
        });
        setShowEnrollDialog(false);
        fetchAutopayStatus();
        if (onEnrollmentChange) onEnrollmentChange();
        
        // Reset form
        setEnrollmentForm({
          property_id: '',
          unit_id: '',
          amount: '',
          frequency: 'monthly',
          start_date: '',
          card_number: '',
          card_exp_month: '',
          card_exp_year: '',
          card_cvv: '',
          send_receipt_email: true,
          send_reminder_email: true
        });
      }
    } catch (error) {
      console.error('Error enrolling in autopay:', error);
      toast.error(error.response?.data?.detail || 'Failed to enroll in automatic payments');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEnrollment = async (scheduleId) => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel automatic payments?\n\nYour subscription will remain active until the end of your current billing period.\n\nAfter that, you will need to make manual payments.'
    );
    
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/tenant/subscription/cancel`,
        { 
          schedule_id: scheduleId,
          reason: 'Cancelled by tenant',
          cancel_immediately: false  // Cancel at period end
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Automatic payments cancelled', {
          description: 'Your subscription will remain active until the end of your current billing period.'
        });
        fetchAutopayStatus();
        if (onEnrollmentChange) onEnrollmentChange();
      }
    } catch (error) {
      console.error('Error cancelling autopay:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel automatic payments');
    }
  };

  const detectCardType = (cardNumber) => {
    const number = cardNumber.replace(/\s/g, '');
    if (/^4/.test(number)) return 'visa';
    if (/^5[1-5]/.test(number)) return 'mastercard';
    if (/^3[47]/.test(number)) return 'amex';
    if (/^6(?:011|5)/.test(number)) return 'discover';
    return 'card';
  };

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\s/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : cleaned;
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800"><Check className="h-3 w-3 mr-1" /> Active</Badge>;
      case 'pending_activation':
        return <Badge className="bg-yellow-100 text-yellow-800"><AlertCircle className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'cancelled':
        return <Badge className="bg-gray-100 text-gray-800"><X className="h-3 w-3 mr-1" /> Cancelled</Badge>;
      case 'past_due':
        return <Badge className="bg-red-100 text-red-800"><AlertCircle className="h-3 w-3 mr-1" /> Past Due</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Automatic Payments
            </CardTitle>
            <CardDescription className="mt-2">
              Set up automatic monthly payments - never miss rent or HOA dues
            </CardDescription>
          </div>
          <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <DollarSign className="h-4 w-4 mr-2" />
                Set Up AutoPay
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Set Up Automatic Payments</DialogTitle>
                <p className="text-sm text-gray-600 mt-2">
                  Your card will be charged automatically each month. You can cancel anytime.
                </p>
              </DialogHeader>
              <form onSubmit={handleEnrollment} className="space-y-4 mt-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <Check className="h-4 w-4 inline mr-2" />
                    Secure payment powered by Stripe
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Monthly Amount ($) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={enrollmentForm.amount}
                      onChange={(e) => setEnrollmentForm({...enrollmentForm, amount: e.target.value})}
                      placeholder="1500.00"
                      required
                    />
                  </div>
                  <div>
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={enrollmentForm.start_date}
                      onChange={(e) => setEnrollmentForm({...enrollmentForm, start_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 flex items-center">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Payment Card Details
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <Label>Card Number *</Label>
                      <Input
                        type="text"
                        value={enrollmentForm.card_number}
                        onChange={(e) => {
                          const formatted = formatCardNumber(e.target.value);
                          if (formatted.replace(/\s/g, '').length <= 16) {
                            setEnrollmentForm({...enrollmentForm, card_number: formatted});
                          }
                        }}
                        placeholder="4242 4242 4242 4242"
                        maxLength="19"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Enter your 16-digit card number</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Exp. Month *</Label>
                        <Input
                          type="text"
                          maxLength="2"
                          value={enrollmentForm.card_exp_month}
                          onChange={(e) => setEnrollmentForm({...enrollmentForm, card_exp_month: e.target.value})}
                          placeholder="12"
                          required
                        />
                      </div>
                      <div>
                        <Label>Exp. Year *</Label>
                        <Input
                          type="text"
                          maxLength="4"
                          value={enrollmentForm.card_exp_year}
                          onChange={(e) => setEnrollmentForm({...enrollmentForm, card_exp_year: e.target.value})}
                          placeholder="2025"
                          required
                        />
                      </div>
                      <div>
                        <Label>CVV *</Label>
                        <Input
                          type="text"
                          maxLength="4"
                          value={enrollmentForm.card_cvv}
                          onChange={(e) => setEnrollmentForm({...enrollmentForm, card_cvv: e.target.value.replace(/\D/g, '')})}
                          placeholder="123"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="receipt_email"
                      checked={enrollmentForm.send_receipt_email}
                      onChange={(e) => setEnrollmentForm({...enrollmentForm, send_receipt_email: e.target.checked})}
                      className="rounded"
                    />
                    <Label htmlFor="receipt_email" className="text-sm cursor-pointer">
                      Email me a receipt after each payment
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="reminder_email"
                      checked={enrollmentForm.send_reminder_email}
                      onChange={(e) => setEnrollmentForm({...enrollmentForm, send_reminder_email: e.target.checked})}
                      className="rounded"
                    />
                    <Label htmlFor="reminder_email" className="text-sm cursor-pointer">
                      Send me a reminder 3 days before each payment
                    </Label>
                  </div>
                </div>

                <DialogFooter className="flex space-x-2">
                  <Button type="button" variant="outline" onClick={() => setShowEnrollDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                    {loading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <>Activate AutoPay</>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : enrollments.length > 0 ? (
          <div className="space-y-4">
            {enrollments.map((enrollment) => (
              <div key={enrollment.id} className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getStatusBadge(enrollment.schedule?.status)}
                      <Badge variant="outline" className="text-xs">
                        {enrollment.schedule?.processor?.toUpperCase() || 'STRIPE'}
                      </Badge>
                    </div>
                    <p className="font-semibold text-xl">
                      ${enrollment.schedule?.amount?.toFixed(2) || '0.00'}
                      <span className="text-sm text-gray-600 font-normal"> per {enrollment.schedule?.frequency || 'month'}</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      <CreditCard className="h-3 w-3 inline mr-1" />
                      {enrollment.card_type?.toUpperCase()} ending in {enrollment.card_last_4}
                    </p>
                    <p className="text-sm text-gray-600">
                      <Calendar className="h-3 w-3 inline mr-1" />
                      Next payment: {enrollment.schedule?.next_payment_date ? 
                        new Date(enrollment.schedule.next_payment_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : 
                        'Processing...'}
                    </p>
                    {enrollment.schedule?.total_payments_made > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {enrollment.schedule.total_payments_made} payment{enrollment.schedule.total_payments_made > 1 ? 's' : ''} completed
                      </p>
                    )}
                  </div>
                  {enrollment.schedule?.status === 'active' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleCancelEnrollment(enrollment.schedule?.id)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <DollarSign className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No automatic payments set up</p>
            <p className="text-sm mt-2">Set up AutoPay to never worry about missing a payment</p>
            <Button 
              className="mt-4 bg-blue-600 hover:bg-blue-700"
              onClick={() => setShowEnrollDialog(true)}
            >
              Get Started
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StripeAutopayEnrollment;
